import { useQueryClient } from '@tanstack/react-query';
import { Links } from 'application/constants/links';
import { Breadcrumbs } from 'components/uikit/Breadcrumbs';
import { Button, ButtonColor } from 'components/uikit/buttons/Button';
import { Input } from 'components/uikit/inputs/Input';
import { Loading } from 'components/uikit/suspense/Loading';
import { useAdvancedForm } from 'helpers/form/useAdvancedForm';
import { useNavigate } from 'react-router-dom';
import { QueryFactory } from 'services/api';
import styles from './singlePage.module.scss';
import React, { FormEvent, useEffect, useRef, useState } from 'react';
import { CustomModal } from '../../../components/uikit/modal/CustomModal.tsx';
import { useScopedTranslation } from '../../../application/localization/useScopedTranslation.ts';
import { useWatch } from 'react-hook-form';
import { requiredRule } from '../../../helpers/form/react-hook-form-helper.ts';
import { CmsPageQuery, SortOrder } from 'services/api/api-client.ts';
import { UseTranslationResponse } from 'react-i18next';
import {
  DataGridRef,
  IColumnProps,
  Pager,
  Scrolling,
} from 'devextreme-react/data-grid';
import clsx from 'clsx';
import formStyles from '../../../components/form/form.module.scss';
import gridStyles from '../../../components/devexpress/Grid/grid.module.scss';
import { DXG, useDataGrid } from 'components/devexpress/Grid/dx-grid';
import BinImage from 'assets/icons/bin.svg?react';
import EditImage from 'assets/icons/edit.svg?react';
import { IconButton } from '../../../components/uikit/IconButton.tsx';
import PlusIcon from 'assets/icons/plus-white.svg';
import Search from 'assets/icons/search-white.svg?react';
import Cross from 'assets/icons/close-button-small.svg?react';
import {
  BooleanParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'react-router-url-params';
import { pagingSortingQueryParams } from 'helpers/pagination-helper.ts';
import { AppPagination } from '../../../components/uikit/pagination/AppPagination.tsx';
import {
  onContextMenuPreparing,
  TableHeaderCell,
} from '../../../helpers/tableHeaderCell/TableHeaderCell.tsx';
import { headerSortClick } from '../../../helpers/sort-helper.ts';
import { NegativeActionText } from '../../../components/uikit/NegativeActionText.tsx';
import { formatDate } from '../../../helpers/abas-helper.ts';
import { TablePlaceholder } from '../../../components/uikit/table/TablePlaceholder.tsx';
import { Tip } from '../../../components/uikit/Tip.tsx';
import CopyIcon from '../../../assets/icons/copy.svg';

type DeletePageModalInfo = {
  visible: boolean;
  pageName: string;
  pageUrl: string;
  id: number | null;
};

export type Form = {
  pageUrl: string;
  generalSearch: string;
};

const pageSizes = [10, 50, 100];

export const DesignerRoot = () => {
  const [params, setParams] = useQueryParams({
    generalSearch: StringParam,
    ...pagingSortingQueryParams(pageSizes[0], true),
    sortBy: withDefault(StringParam, 'updatedAt'),
    desc: withDefault(BooleanParam, true),
  });

  const queryClient = useQueryClient();
  const customPagesQuery = CmsPageQuery.useSearchQuery(
    params.generalSearch?.trim(),
    undefined,
    undefined,
    undefined,
    (params.page - 1) * params.perPage,
    params.perPage,
    params.sortBy ?? 'updatedAt',
    params.desc ? SortOrder.Desc : SortOrder.Asc,
    {
      placeholderData: (previousData) => previousData,
      throwOnError: false,
      staleTime: 30 * 1000,
    },
  );

  useEffect(() => {
    void queryClient.invalidateQueries({
      queryKey: QueryFactory.CmsPageQuery.searchQueryKey(),
    });
  }, []);
  const navigate = useNavigate();
  const i18n = useScopedTranslation('Page.PageDesigner');

  useDataGrid();

  const [deletePageModalInfo, setDeletePageModalInfo] =
    useState<DeletePageModalInfo>({
      visible: false,
      pageName: '',
      pageUrl: '',
      id: null,
    });
  const [isLoading, setIsLoading] = useState(false);

  const form = useAdvancedForm<Form>(
    async () => {
      if (deletePageModalInfo.pageUrl === pageUrl) {
        closeModal();
        setIsLoading(true);
        await QueryFactory.CmsPageClient.deleteById(deletePageModalInfo.id!);
        await queryClient.invalidateQueries({
          queryKey: QueryFactory.CmsPageQuery.searchQueryKey(),
        });
        setIsLoading(false);
      } else {
        form.setError('pageUrl', {
          message: i18n.t('wrong_url'),
        });
      }
    },
    {
      defaultValues: {
        pageUrl: '',
        generalSearch: params.generalSearch ?? '',
      },
    },
  );

  const [pageUrl, generalSearch] = useWatch({
    control: form.control,
    name: ['pageUrl', 'generalSearch'],
  });

  const closeModal = () => {
    setDeletePageModalInfo({
      visible: false,
      pageName: '',
      pageUrl: '',
      id: null,
    });
    form.reset();
  };

  const handleSubmit = async () => {
    const result = await form.trigger('pageUrl');
    if (result) {
      void form.handleSubmitDefault();
    }
  };

  const changeSorting = (sortBy: string) => {
    setParams((prevParams) => ({
      ...prevParams,
      sortBy,
      desc: prevParams.sortBy !== sortBy ? false : !prevParams.desc,
      page: 1,
    }));
  };

  const changePagination = (pagination: {
    page?: number;
    perPage?: number;
  }) => {
    setParams((prevParams) => ({
      ...prevParams,
      ...(pagination.page !== undefined && { page: pagination.page }),
      ...(pagination.perPage !== undefined && { perPage: pagination.perPage }),
    }));
  };

  const numberOfRows = customPagesQuery?.data?.data.length ?? 0;

  const dataGridRef = useRef<DataGridRef<any, number>>(null);
  const columnsConfig = getColumnsConfig(i18n);

  const paginationProps = {
    page: params.page,
    perPage: params.perPage,
    totalCount: customPagesQuery.data?.totalCount ?? 0,
    pageSizes: pageSizes,
    numberOfRows: numberOfRows,
    changePagination: changePagination,
  };

  const searchByTitleAndUrl = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setParams((prevParams) => ({
      ...prevParams,
      page: 1,
      generalSearch: form.getValues('generalSearch'),
    }));
  };

  const resetFilters = () => {
    form.reset({
      generalSearch: '',
    });

    setParams((prevParams) => ({
      ...prevParams,
      page: 1,
      generalSearch: form.getValues('generalSearch'),
    }));
  };

  const isResetButtonShow = !!params.generalSearch;

  const isPlaceholderVisible =
    !numberOfRows &&
    !customPagesQuery.isLoading &&
    !customPagesQuery.isPlaceholderData;

  return (
    <Loading loading={isLoading}>
      <div>
        <Breadcrumbs customLinkForHomeIcon={Links.Authorized.Admin.route} />
        {deletePageModalInfo.visible && (
          <CustomModal
            isOpen={true}
            onSubmit={handleSubmit}
            onCancel={closeModal}
            cancelButtonText={i18n.t('cancel')}
            submitButtonText={i18n.t('delete_page')}
            submitButtonType={ButtonColor.Warning}
            titleText={i18n.t('delete_page')}
            containerClassName={styles.modalBodyWidth480}
          >
            <div className={styles.marginBottom10}>
              {i18n.t('delete_modal_body', {
                pageInfo: `«${deletePageModalInfo.pageName}» (${deletePageModalInfo.pageUrl})`,
              })}
            </div>
            <Input
              {...form.register(
                'pageUrl',
                requiredRule(i18n.t('required_error')),
              )}
              onChange={(e) => {
                form.clearErrors();
                form.setValue('pageUrl', e.target.value);
              }}
              errorText={form.formState.errors.pageUrl?.message}
              fullWidth={true}
            />
          </CustomModal>
        )}
        <div
          className={clsx(
            styles.rowContainer,
            styles.marginBottom25,
            styles.spaceBetween,
            styles.columnGap20,
          )}
        >
          <form onSubmit={searchByTitleAndUrl}>
            <div className={clsx(styles.rowContainer, styles.columnGap15)}>
              <Input
                {...form.register('generalSearch')}
                placeholder={i18n.t('search')}
                className={clsx(styles.width400, styles.paddingRight50)}
                endAdornment={
                  <div className={clsx(styles.rowContainer, styles.columnGap8)}>
                    {generalSearch && (
                      <IconButton
                        type={'destroy'}
                        iconClassName={clsx(styles.cursorPointer)}
                        onClick={() => form.setValue('generalSearch', '')}
                      >
                        <Cross />
                      </IconButton>
                    )}
                    <Search />
                  </div>
                }
                endAdornmentClassname={styles.cursorDefault}
              />
              <Button
                type={'submit'}
                className={clsx(formStyles.actionButton)}
                title={i18n.t('search_button')}
              />
              {isResetButtonShow && (
                <NegativeActionText
                  text={i18n.t('reset')}
                  onClickHandler={resetFilters}
                  containerClassName={styles.marginLeft10}
                />
              )}
            </div>
          </form>
          <Button
            title={i18n.t('new_page')}
            className={styles.paddingLeft}
            onClick={() =>
              navigate(
                Links.Authorized.CustomPages.DesignerNewPage.link({
                  url: 'new',
                }),
              )
            }
            icon={PlusIcon}
          />
        </div>
        <Loading loading={customPagesQuery.isFetching}>
          <DXG.DataGrid
            ref={dataGridRef}
            dataSource={customPagesQuery.data?.data}
            className={clsx(
              gridStyles.editorOutline,
              formStyles.table,
              styles.table,
            )}
            sorting={{ mode: 'none' }}
            paging={{ pageSize: customPagesQuery.data?.data.length }}
            noDataText={''}
            onContextMenuPreparing={onContextMenuPreparing}
            onCellClick={(event) => headerSortClick(event, changeSorting)}
          >
            <Scrolling
              columnRenderingMode={'virtual'}
              showScrollbar={'always'}
            />
            {columnsConfig.map((x, index) => {
              return (
                <DXG.Column
                  cssClass={clsx(x.cssClass, formStyles.text)}
                  key={index}
                  showEditorAlways={x.allowEditing}
                  headerCellRender={(cellInfo) => (
                    <TableHeaderCell
                      dataField={cellInfo.column.dataField}
                      caption={cellInfo.column.caption}
                      sortBy={params.sortBy}
                      desc={params.desc}
                    />
                  )}
                  {...x}
                />
              );
            })}
            <DXG.Column
              width={80}
              cssClass={clsx(formStyles.text, styles.verticalAlignCenter)}
              cellRender={(cell) => {
                return (
                  <div
                    className={clsx(styles.rowContainer, styles.columnGap20)}
                  >
                    <IconButton
                      type={'destroy'}
                      iconClassName={styles.heightWidthUnset}
                      onClick={() =>
                        setDeletePageModalInfo({
                          visible: true,
                          pageName: cell.data.title,
                          pageUrl: cell.data.url,
                          id: cell.data.id,
                        })
                      }
                    >
                      <BinImage />
                    </IconButton>
                    <IconButton
                      type={'primary'}
                      iconClassName={styles.heightWidthUnset}
                      className={styles.marginBottom2}
                      onClick={() => {
                        void navigate(
                          Links.Authorized.CustomPages.DesignerSinglePage.link({
                            url: cell.data.url,
                          }),
                        );
                      }}
                    >
                      <EditImage />
                    </IconButton>
                  </div>
                );
              }}
            />
            <Pager visible={false} />
          </DXG.DataGrid>
          {isPlaceholderVisible && <TablePlaceholder />}
          {!!numberOfRows && <AppPagination {...paginationProps} />}
        </Loading>
      </div>
    </Loading>
  );
};

const getColumnsConfig = (
  i18n: UseTranslationResponse<'translation', 'Page.PageDesigner'>,
): (IColumnProps & { disableExport?: boolean })[] => [
  {
    dataField: 'title',
    allowEditing: false,
    allowSorting: true,
    caption: i18n.t('title'),
    cssClass: clsx(formStyles.textCellPadding, formStyles.tableHeaderPointer),
    minWidth: 300,
  },
  {
    dataField: 'url',
    allowEditing: false,
    allowSorting: true,
    caption: i18n.t('url'),
    cssClass: clsx(formStyles.textCellPadding, formStyles.tableHeaderPointer),
    minWidth: 300,
    cellRender: (cell) => {
      const pageUrlReadOnly = window.location.origin + '/' + cell.value;

      return (
        <div className={formStyles.rowContainer4}>
          <Tip
            textBody={
              <div
                className={formStyles.url}
                onClick={() => window.open(`/${cell.value}`, '_blank')}
              >
                {cell.value}
              </div>
            }
            tooltipText={i18n.t('go_to') + pageUrlReadOnly}
            className={formStyles.backgroundUnset}
          />
          <Tip
            tooltipText={i18n.t('copy')}
            customImage={CopyIcon}
            className={clsx(formStyles.backgroundUnset)}
            onClick={() => navigator.clipboard.writeText(pageUrlReadOnly)}
          />
        </div>
      );
    },
  },
  {
    dataField: 'updatedAt',
    allowEditing: false,
    allowSorting: true,
    caption: i18n.t('last_modified'),
    cssClass: clsx(formStyles.textCellPadding, formStyles.tableHeaderPointer),
    width: 200,
    cellRender: (cell) => formatDate(cell.value),
  },
];
